import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

interface ContentItem {
  id: string;
  url: string;
  domain: string;
  title: string | null;
  consent_type: string;
  consent_signal: string | null;
  license: string | null;
  author: string | null;
  consent_confidence: number;
  crawled_at: string;
  removed_at: string | null;
  removal_reason: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function ContentExplorer() {
  const [content, setContent] = useState<ContentItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [domain, setDomain] = useState('');
  const [consentType, setConsentType] = useState('');
  const [includeRemoved, setIncludeRemoved] = useState(false);

  useEffect(() => {
    fetchContent();
  }, [pagination.page, domain, consentType, includeRemoved]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });

      if (search) params.set('q', search);
      if (domain) params.set('domain', domain);
      if (consentType) params.set('consent_type', consentType);
      if (includeRemoved) params.set('include_removed', 'true');

      const response = await fetch(`${API_BASE_URL}/admin/content?${params}`);
      if (!response.ok) throw new Error('Failed to fetch content');
      
      const data = await response.json();
      setContent(data.content || []);
      setPagination(data.pagination);
    } catch (err) {
      setError('Failed to load content');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPagination((prev) => ({ ...prev, page: 1 }));
    fetchContent();
  };

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <h1>Content Explorer</h1>
        <p>Browse and manage indexed content</p>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="filter-bar">
        <input
          type="text"
          className="input"
          placeholder="Search URLs or titles..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <input
          type="text"
          className="input"
          placeholder="Filter by domain..."
          value={domain}
          onChange={(e) => setDomain(e.target.value)}
          style={{ maxWidth: '200px' }}
        />
        <select
          value={consentType}
          onChange={(e) => {
            setConsentType(e.target.value);
            setPagination((prev) => ({ ...prev, page: 1 }));
          }}
        >
          <option value="">All consent types</option>
          <option value="content-signal">Content Signal</option>
          <option value="creative-commons">Creative Commons</option>
          <option value="public-domain">Public Domain</option>
          <option value="registry">Registry</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <input
            type="checkbox"
            checked={includeRemoved}
            onChange={(e) => {
              setIncludeRemoved(e.target.checked);
              setPagination((prev) => ({ ...prev, page: 1 }));
            }}
          />
          Include removed
        </label>
        <button type="submit" className="btn btn-primary">
          Search
        </button>
      </form>

      {/* Results */}
      {loading ? (
        <div className="loading-state">Loading content...</div>
      ) : error ? (
        <div className="empty-state">
          <p>{error}</p>
          <button className="btn btn-primary mt-4" onClick={fetchContent}>
            Retry
          </button>
        </div>
      ) : content.length === 0 ? (
        <div className="card empty-state">
          <p>No content found</p>
          <p className="text-secondary mt-2">
            Try adjusting your filters or add some domains to crawl.
          </p>
        </div>
      ) : (
        <>
          <table className="data-table">
            <thead>
              <tr>
                <th>Title / URL</th>
                <th>Domain</th>
                <th>Consent</th>
                <th>License</th>
                <th>Confidence</th>
                <th>Crawled</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {content.map((item) => (
                <tr key={item.id}>
                  <td className="truncate">
                    <Link to={`/admin/content/${item.id}`}>
                      {item.title || item.url}
                    </Link>
                    {item.title && (
                      <div className="text-secondary" style={{ fontSize: '0.75rem' }}>
                        {item.url}
                      </div>
                    )}
                  </td>
                  <td>
                    <Link to={`/admin/domains/${item.domain}`}>
                      {item.domain}
                    </Link>
                  </td>
                  <td>
                    <span className="badge badge-success">{item.consent_type}</span>
                  </td>
                  <td>{item.license || '-'}</td>
                  <td>{Math.round(item.consent_confidence * 100)}%</td>
                  <td>{new Date(item.crawled_at).toLocaleDateString()}</td>
                  <td>
                    {item.removed_at ? (
                      <span className="badge badge-error">Removed</span>
                    ) : (
                      <span className="badge badge-success">Active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="pagination">
            <button
              className="pagination-btn"
              disabled={pagination.page <= 1}
              onClick={() => handlePageChange(pagination.page - 1)}
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} items)
            </span>
            <button
              className="pagination-btn"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => handlePageChange(pagination.page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default ContentExplorer;
